import { configure, toJS } from "mobx";
import { Provider } from "mobx-react";
import * as React from "react";
import * as ReactDOM from "react-dom";

import configStore from "./stores/ConfigStore";
import contextStore from "./stores/ContextStore";
import navigationStore from "./stores/NavigationStore";
import panelStore from "./stores/PanelStore";
import notificationStore from "./stores/NotificationStore";

import App from "./app/App";
import "./index.css";

import { PredefinedRole } from "./domain/auth/PredefinedRole";
import signal from "./services/site/signaling";
import signaling from "./services/site/signaling";

import { PaletteType } from "@material-ui/core";
import { KeyDownMessage, PredefinedColor } from "meet-addons-sdk";
import { PredefinedAuthProvider } from "./app/enums/PredefinedAuthProvider";
import meDataService from "./services/auth/MeDataService";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

import { PredefinedEnvironment } from "./app/enums/PredefinedEnvironment";
import { ITokenInfo } from "./domain/auth/TokenInfo";

import * as microsoftTeams from "@microsoft/teams-js";
import { isTeamsAddonMode } from "./utils";

import { PredefinedResourceType } from "./domain/api/meetings/PredefinedResourceType";
import tokenRefresher from "./services/auth/TokenRefreshingService";
import AddonMessanger from "./app/workspace/AddonMessanger";

if (isTeamsAddonMode) {
  microsoftTeams.initialize();
  microsoftTeams.registerOnThemeChangeHandler((teamsTheme: string) => {
    const me = toJS(configStore.me);
    me!.theme = teamsTheme !== "dark" ? "light" : "dark";
    configStore.setMe(me!);
    return meDataService.patchAsync({
      theme: me!.theme,
    });
  });
}

if (!window.vivani.environment.toLowerCase().startsWith("dev")) {
  Sentry.init({
    dsn: "https://60003abdff3b4d2e9868e937b4afefb0@o327792.ingest.sentry.io/5955243",
    environment: window.vivani.environment,
    release: window.vivani.version,
    integrations: [new Integrations.BrowserTracing()],

    // We recommend adjusting this value in production, or using tracesSampler
    // for finer control
    tracesSampleRate: 0.0,
  });
}

configure({
  enforceActions: "always",
});

const meetConfig = window.vivani.meet;
contextStore.setCurrentResource(meetConfig.ctx);

configStore.init(window.vivani);

signaling.onContextSync = (e) => {
  signaling.contextInfo = e;
  signaling.onMeetingStateChanged(e);
};

tokenRefresher.monitorAsync().then(async () => {
  await configStore.fetchAsync();
  const contextTask = contextStore.initAsync();
  const signalTask = signal.initAsync(true);

  Promise.all([contextTask, signalTask]).then(async () => {
    await updateAnonDataAsync();
    ReactDOM.render(Root, document.getElementById("root") as HTMLElement);
  });
});

const Root = (
  <Provider
    configStore={configStore}
    contextStore={contextStore}
    navigationStore={navigationStore}
    panelStore={panelStore}
    notificationStore={notificationStore}
  >
    <App />
  </Provider>
);

const updateAnonDataAsync = () => {
  if (
    configStore.me!.authProviders.length !== 1 ||
    configStore.me!.authProviders[0] !== PredefinedAuthProvider.Guest
  ) {
    return Promise.resolve();
  }

  const sessionData = sessionStorage.getItem("anon_user_data");
  if (!sessionData) {
    return Promise.resolve();
  }

  const userData = JSON.parse(sessionData) as IUserData;
  if (!userData) {
    return Promise.resolve();
  }

  const me = toJS(configStore.me);
  me!.color = userData.color;
  me!.familyName = userData.lastName;
  me!.firstName = userData.firstName;
  me!.theme = userData.theme;
  configStore.setMe(me!);

  return meDataService.patchAsync({
    color: userData.color,
    familyName: userData.lastName,
    firstName: userData.firstName,
    theme: userData.theme,
  });
};

const handleKeyboardEvent = (ev: KeyboardEvent) => {
  const message = new KeyDownMessage();
  message.altKey = ev.altKey;
  message.ctrlKey = ev.ctrlKey;
  message.keyCode = ev.keyCode;
  message.metaKey = ev.metaKey;
  message.shiftKey = ev.shiftKey;
  message.returnValue = ev.returnValue;

  contextStore.setKeyDownMessage(message);
  AddonMessanger.sendKeyDownMessageToAddons(message);
};

document.documentElement.addEventListener("keydown", handleKeyboardEvent);

declare global {
  interface IHostConfig {
    auth: string;
    api: string;
    cdn: string;
    web: string;
    tx: string;
    xp: string;
  }

  interface IResourceContext {
    rtype: PredefinedResourceType;
    rid: string;
    role: PredefinedRole;
    tenantName: string;
    tenantLogo: string;
  }

  interface IMeetConfig {
    hosts: IHostConfig;
    ctx: IResourceContext;
  }

  interface IVivani {
    environment: PredefinedEnvironment;
    version: string;
    meet: IMeetConfig;
    token: ITokenInfo;
  }

  // tslint:disable-next-line:interface-name
  interface Window {
    vivani: IVivani;
  }
}

interface IUserData {
  firstName: string;
  lastName: string;
  color: PredefinedColor;
  theme: PaletteType;
}
